/**
 * OTA apply orchestrator — pairs download state with route-aware policy.
 *
 * Apply rules (Apple / enterprise feel):
 *   1. Critical flows (check-in, checkout, KYC, listing wizard): never reload,
 *      never banner. Download can finish in the background; apply waits.
 *   2. Auth surfaces (welcome, login, phone OTP…): silent apply after a short
 *      settle — hotfixes must not require a banner tap on the login path —
 *      EXCEPT while auth is in flight or inside OTA_AUTH_GRACE_MS of the
 *      session landing. `reloadAsync` restarts the app (splash + permission
 *      dialogs); doing it 500 ms after login reads as "logged in twice".
 *   3. Resume from background on a non-critical route: silent apply
 *      ("update landed while you were away"), subject to the same auth guard.
 *   4. Active browsing (explore, trips, profile): soft banner — person
 *      chooses the moment; one tap, no modal interruption.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { AppState, type AppStateStatus } from 'react-native'
import { usePathname } from 'expo-router'
import { UpdateReadyBanner } from '@/components/ui/UpdateReadyBanner'
import { useAuthStore } from '@/lib/stores/auth.store'
import {
  isOtaAuthGraceActive,
  isOtaCriticalRoute,
  isOtaSilentApplyRoute,
  OTA_AUTH_GRACE_MS,
  shouldOfferOtaBanner,
  shouldSilentlyApplyOta,
} from '@/lib/updates/ota-policy'

/** Brief settle so navigation / keyboard finish before reload. */
const SILENT_APPLY_SETTLE_MS = 500
/**
 * Ignore app-switch flickers (control center, share sheet). Only treat a real
 * "stepped away" as resume-from-background — Apple-like, not thrashy.
 */
const MIN_BACKGROUND_MS_FOR_RESUME_APPLY = 2_000

interface OtaUpdateControllerProps {
  pending: boolean
  apply: () => Promise<void>
}

export function OtaUpdateController({ pending, apply }: OtaUpdateControllerProps) {
  const pathname = usePathname()
  const applyingRef = useRef(false)
  const backgroundedAtRef = useRef<number | null>(null)
  const applyRef = useRef(apply)
  applyRef.current = apply
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname
  // Auth signals for the apply guard. `isCompletingSignup` covers the login/OTP
  // handoff itself; `lastAuthenticatedAt` covers the window right after the
  // session lands, where a reload still restarts the app over the user.
  const authInFlight = useAuthStore((s) => s.isCompletingSignup)
  const lastAuthenticatedAt = useAuthStore((s) => s.lastAuthenticatedAt)
  const authInFlightRef = useRef(authInFlight)
  authInFlightRef.current = authInFlight
  const lastAuthenticatedAtRef = useRef(lastAuthenticatedAt)
  lastAuthenticatedAtRef.current = lastAuthenticatedAt
  // Tiempo que pasa no es un cambio de estado: este tick re-arma el efecto
  // cuando la ventana post-auth se agota en una superficie silenciosa.
  const [policyTick, setPolicyTick] = useState(0)

  const runApply = useCallback((reason: string) => {
    if (applyingRef.current) return
    applyingRef.current = true
    if (__DEV__) console.log('[ota] apply', reason, pathnameRef.current)
    void applyRef.current().finally(() => {
      // If reload fails, allow a later attempt.
      applyingRef.current = false
    })
  }, [])

  // Silent apply on auth / idle surfaces — but never over an authentication.
  useEffect(() => {
    if (!pending || applyingRef.current) return
    if (isOtaCriticalRoute(pathname)) return
    if (!isOtaSilentApplyRoute(pathname)) return

    const now = Date.now()
    if (shouldSilentlyApplyOta({ pending, pathname, authInFlight, lastAuthenticatedAt, now })) {
      const timer = setTimeout(() => {
        runApply('silent-auth-surface')
      }, SILENT_APPLY_SETTLE_MS)
      return () => clearTimeout(timer)
    }

    // Auth en vuelo o dentro de la ventana post-auth: esperar y re-evaluar,
    // en vez de reiniciar la app encima del login que acaba de terminar.
    if (authInFlight) return undefined
    const remainingMs = OTA_AUTH_GRACE_MS - (now - (lastAuthenticatedAt ?? now))
    const timer = setTimeout(() => setPolicyTick((tick) => tick + 1), Math.max(0, remainingMs) + 50)
    return () => clearTimeout(timer)
  }, [pending, pathname, runApply, authInFlight, lastAuthenticatedAt, policyTick])

  // Silent apply when returning from a real background stretch on a non-critical route.
  useEffect(() => {
    const onChange = (state: AppStateStatus) => {
      if (state === 'background' || state === 'inactive') {
        if (backgroundedAtRef.current == null) backgroundedAtRef.current = Date.now()
        return
      }
      if (state !== 'active') return
      const awaySince = backgroundedAtRef.current
      backgroundedAtRef.current = null
      if (awaySince == null) return
      if (Date.now() - awaySince < MIN_BACKGROUND_MS_FOR_RESUME_APPLY) return
      if (!pending || applyingRef.current) return
      if (isOtaCriticalRoute(pathnameRef.current)) return
      // Volver del background no autoriza a reiniciar sobre una auth recién
      // hecha (p. ej. login con el permiso de notificaciones en primer plano).
      if (authInFlightRef.current) return
      if (isOtaAuthGraceActive(lastAuthenticatedAtRef.current, Date.now())) return
      runApply('resume-from-background')
    }

    const sub = AppState.addEventListener('change', onChange)
    return () => sub.remove()
  }, [pending, runApply])

  const showBanner = pending && shouldOfferOtaBanner(pathname)

  return (
    <UpdateReadyBanner
      visible={showBanner}
      onApply={() => {
        if (isOtaCriticalRoute(pathname)) return
        runApply('banner-tap')
      }}
    />
  )
}
